import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX, FiSun, FiMoon, FiUploadCloud, FiDownload } from "react-icons/fi";
import { useTheme } from "./ThemProvider";
import { Link as ScrollLink } from "react-scroll";
import RifathLogo from "./RifathLogo";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const navLinks = [
  { name: "Home", to: "hero" },
  { name: "About", to: "about" },
  { name: "Skills", to: "skills" },
  { name: "Services", to: "services" },
  { name: "Projects", to: "projects" },
  { name: "Certifications", to: "certification" },
  { name: "Contact", to: "contact" },
];

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cvDownloadUrl, setCvDownloadUrl] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState("hero");

  const auth = getAuth();
  const storage = getStorage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, [auth]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleCvUpload = async (file: File) => {
    if (!file || !isLoggedIn) return;
    try {
      const storageRef = ref(storage, `cv/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setCvDownloadUrl(downloadUrl);
    } catch (error) {
      console.error("Error uploading CV:", error);
    }
  };

  useEffect(() => {
    const fetchCvDownloadUrl = async () => {
      const storageRef = ref(storage, "cv/sample-cv.pdf");
      try {
        const downloadUrl = await getDownloadURL(storageRef);
        setCvDownloadUrl(downloadUrl);
      } catch (error) {
        console.error("Error fetching CV download URL:", error);
      }
    };
    fetchCvDownloadUrl();
  }, []);

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-white/80 dark:bg-surface-900/80 backdrop-blur-xl shadow-lg shadow-black/5 dark:shadow-black/20 border-b border-gray-200/50 dark:border-gray-800/50"
          : "py-5 bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <ScrollLink to="hero" smooth={true} duration={500} className="cursor-pointer">
          <motion.div
            className="flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RifathLogo size={40} animated={true} />
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              Rifath<span className="text-primary-500">.</span>
            </span>
          </motion.div>
        </ScrollLink>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <ScrollLink
              key={link.to}
              to={link.to}
              smooth={true}
              duration={500}
              offset={-80}
              spy={true}
              onSetActive={() => setActiveSection(link.to)}
              className="relative px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all duration-300 group"
            >
              <span
                className={`relative z-10 transition-colors duration-300 ${
                  activeSection === link.to
                    ? "text-primary-600 dark:text-primary-400"
                    : scrolled
                    ? "text-gray-600 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400"
                    : "text-gray-700 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400"
                }`}
              >
                {link.name}
              </span>
              {/* Animated underline */}
              {activeSection === link.to && (
                <motion.div
                  className="absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                  layoutId="activeNavUnderline"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {/* Hover background */}
              <span className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                scrolled
                  ? "bg-primary-50 dark:bg-primary-950/50"
                  : "bg-white/50 dark:bg-white/5"
              }`} />
            </ScrollLink>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-3">
          {cvDownloadUrl && (
            <motion.a
              href={cvDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary !py-2.5 !px-5 !text-sm !rounded-lg"
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiDownload size={16} />
              Resume
            </motion.a>
          )}
          {isLoggedIn && (
            <label
              className="cursor-pointer p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus-visible-ring"
              aria-label="Upload CV"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  (e.currentTarget.querySelector('input') as HTMLInputElement)?.click();
                }
              }}
            >
              <FiUploadCloud size={18} />
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleCvUpload(e.target.files[0]);
                  }
                }}
              />
            </label>
          )}
          {isLoggedIn ? (
            <motion.button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Log Out
            </motion.button>
          ) : (
            <a
              href="/login"
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg transition-colors"
            >
              Log In
            </a>
          )}
          <motion.button
            onClick={toggleTheme}
            className="relative p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 overflow-hidden"
            aria-label="Toggle Theme"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9, rotate: 180 }}
          >
            <AnimatePresence mode="wait">
              {theme === "light" ? (
                <motion.div
                  key="moon"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiMoon size={18} />
                </motion.div>
              ) : (
                <motion.div
                  key="sun"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiSun size={18} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex lg:hidden items-center gap-3">
          <motion.button
            onClick={toggleTheme}
            className="p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle Theme"
            whileTap={{ scale: 0.9, rotate: 180 }}
          >
            {theme === "light" ? <FiMoon size={18} /> : <FiSun size={18} />}
          </motion.button>
          <motion.button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus-visible-ring"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              {menuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <FiX size={22} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <FiMenu size={22} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="lg:hidden overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 py-4 bg-white/95 dark:bg-surface-900/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-800/50 space-y-1">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <ScrollLink
                    to={link.to}
                    smooth={true}
                    duration={500}
                    offset={-80}
                    className="block px-4 py-3 text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded-lg font-medium cursor-pointer transition-colors"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.name}
                  </ScrollLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.3 }}
                className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700 space-y-2"
              >
                {cvDownloadUrl && (
                  <a
                    href={cvDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full justify-center !py-3"
                  >
                    <FiDownload size={16} />
                    Download Resume
                  </a>
                )}
                {isLoggedIn ? (
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 text-center text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                  >
                    Log Out
                  </button>
                ) : (
                  <a
                    href="/login"
                    className="block w-full py-3 text-center text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Log In
                  </a>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
